import { useSelector } from "store/useSelector";
import type { PnlChartSectionType } from "../../../../../types/types";
import {
  useGetPageFromPath,
  usePnlCategoriesForCustomer,
  usePnlPopupTimeRange,
  useFindDataDumpForPnlChart,
  useFindDataDumpForTrendChart,
} from "../../../../../utils/customHooks/genericHooks";
import { calcPnlChartCategories } from "../../../PnlSummaryChart/calcPnlChartCategories";
import { useTrendChartBarsArray } from "utils/customHooks/trendChartHooks";
import { isEqual } from "lodash";
import { useIsMidgardDDAEnabled } from "components/Dashboard/GroupMetrics/useMetrics";
import { useCalcPnlChartCategoriesWithProducingLosses } from "components/Dashboard/PnlSummaryChart/calcPnlChartCategoriesWithProducingLosses";
import { useMemo } from "react";

export function usePnlChartSectionsArr() {
  const calcPnlChartCategoriesWithProducingLosses =
    useCalcPnlChartCategoriesWithProducingLosses();
  const { isTrendsPage } = useGetPageFromPath();
  const pnlPopupTimeRange = usePnlPopupTimeRange();
  const rollupMetricTrendChartBarsArray = useTrendChartBarsArray(isTrendsPage);

  const isMidgardDDAEnabled = useIsMidgardDDAEnabled();
  const rangeToFind = pnlPopupTimeRange;
  const dataDumpWithProducingLosses = useFindDataDumpForPnlChart({
    geToFind: rangeToFind?.ge ?? null,
  });
  const dataDumpNoProducingLosses = useFindDataDumpForTrendChart({
    geToFind: rangeToFind?.ge ?? null,
  });

  const hiddenPnlChartCategories = useSelector(
    state => state.legacy.hiddenPnlChartCategories,
    isEqual
  );
  const pnlCategoriesForCustomer = usePnlCategoriesForCustomer();

  return useMemo(() => {
    const rollupMetric = rollupMetricTrendChartBarsArray.find(({ range }) => {
      return (
        pnlPopupTimeRange &&
        pnlPopupTimeRange.ge <= range.ge &&
        range.ge <= pnlPopupTimeRange.lt
      );
    });
    const dataDumpForPnlPopupRange = isMidgardDDAEnabled
      ? dataDumpWithProducingLosses
      : dataDumpNoProducingLosses;
    const emptyPnlChartSectionsArr = pnlCategoriesForCustomer.map(
      ({ label }) => ({
        title: label,
        time: 0,
        count: null,
        percent: null,
        bars: [],
      })
    );

    let pnlChartSectionsArr: PnlChartSectionType[];

    // only allow data dump on trends page if pnl popup has data
    const isPnlPopupWithData = Boolean(
      pnlPopupTimeRange && dataDumpForPnlPopupRange
    );
    const shouldUseDataDump =
      isMidgardDDAEnabled && (!isTrendsPage || isPnlPopupWithData);
    if (shouldUseDataDump) {
      pnlChartSectionsArr = dataDumpForPnlPopupRange
        ? calcPnlChartCategoriesWithProducingLosses(dataDumpForPnlPopupRange)
        : emptyPnlChartSectionsArr;
    } else {
      pnlChartSectionsArr = rollupMetric
        ? calcPnlChartCategories(rollupMetric, hiddenPnlChartCategories)
        : emptyPnlChartSectionsArr;
    }
    return {
      pnlChartSectionsArr,
      chartDataDump: dataDumpForPnlPopupRange,
    };
  }, [
    calcPnlChartCategoriesWithProducingLosses,
    dataDumpNoProducingLosses,
    dataDumpWithProducingLosses,
    hiddenPnlChartCategories,
    isMidgardDDAEnabled,
    isTrendsPage,
    pnlCategoriesForCustomer,
    pnlPopupTimeRange,
    rollupMetricTrendChartBarsArray,
  ]);
}
